import { useState } from 'react';
import { Card, CardTitle, CardDescription } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { DebugTabs } from '@/components/debug/DebugTabs';
import { TriggerPanel } from '@/components/debug/TriggerPanel';
import { QuartzTable } from '@/components/debug/QuartzTable';
import { QueueTable } from '@/components/debug/QueueTable';
import { ScrapeLog } from '@/components/debug/ScrapeLog';
import { DbStats } from '@/components/debug/DbStats';
import { ErrorLog } from '@/components/debug/ErrorLog';
import { useSession } from '@/lib/session';
import { useDebugSnapshot, useQuartzJobs, useDebugErrors } from '@/lib/queries';

type TabId = 'triggers' | 'quartz' | 'queue' | 'scrapes' | 'db' | 'errors';

export default function Debug() {
  const { data: session } = useSession();
  const [tab, setTab] = useState<TabId>('triggers');
  const isLan = !!session?.isLan;

  const snap = useDebugSnapshot(isLan);
  const quartz = useQuartzJobs(isLan);
  const errs = useDebugErrors(isLan);

  if (!isLan) {
    return (
      <Card>
        <CardTitle>Debug Console</CardTitle>
        <CardDescription className="mt-2">
          Debug console is restricted to trusted LAN/loopback origins.
        </CardDescription>
      </Card>
    );
  }

  const tabs = [
    { id: 'triggers', label: 'Triggers' },
    { id: 'quartz', label: 'Quartz Schedule', count: quartz.data?.length ?? 0 },
    { id: 'queue', label: 'Queue', count: snap.data?.pendingCount ?? 0 },
    { id: 'scrapes', label: 'Scrape Log', count: snap.data?.recentScrapes.length ?? 0 },
    { id: 'db', label: 'DB Stats' },
    { id: 'errors', label: 'Errors', count: errs.data?.recent.length ?? 0 },
  ];

  return (
    <>
      <Card>
        <CardTitle>Debug Console</CardTitle>
        <CardDescription className="mt-2">
          Manual triggers, queue state, DB counts, and Quartz schedule.{' '}
          <span className="text-accent">LAN-only · auto-refresh 8s</span>
        </CardDescription>
      </Card>

      <DebugTabs tabs={tabs} value={tab} onChange={(id) => setTab(id as TabId)} />

      {tab === 'triggers' && (
        snap.isLoading ? <LoadingSkeleton lines={6} /> :
        snap.error ? <ErrorState message={(snap.error as Error).message} /> :
        snap.data ? <TriggerPanel entities={snap.data.linkedInEntities} /> : null
      )}

      {tab === 'quartz' && (
        quartz.isLoading ? <LoadingSkeleton lines={6} /> :
        quartz.error ? <ErrorState message={(quartz.error as Error).message} /> :
        <QuartzTable jobs={quartz.data ?? []} />
      )}

      {tab === 'queue' && (
        snap.isLoading ? <LoadingSkeleton lines={6} /> :
        snap.error ? <ErrorState message={(snap.error as Error).message} /> :
        snap.data ? <QueueTable snapshots={snap.data.snapshots} /> : null
      )}

      {tab === 'scrapes' && (
        snap.isLoading ? <LoadingSkeleton lines={6} /> :
        snap.error ? <ErrorState message={(snap.error as Error).message} /> :
        snap.data ? <ScrapeLog rows={snap.data.recentScrapes} /> : null
      )}

      {tab === 'db' && (
        snap.isLoading ? <LoadingSkeleton lines={6} /> :
        snap.error ? <ErrorState message={(snap.error as Error).message} /> :
        snap.data ? <DbStats rows={snap.data.dbStats} /> : null
      )}

      {tab === 'errors' && (
        errs.isLoading ? <LoadingSkeleton lines={6} /> :
        errs.error ? <ErrorState message={(errs.error as Error).message} /> :
        errs.data ? <ErrorLog data={errs.data} /> : null
      )}
    </>
  );
}
